import React, { useState } from "react";
import TextareaAutosize from "react-autosize-textarea/lib";
import sample from "../../../../public/massNotificationSample.csv";
import { Organization } from "../../organizations/types";
import { useDispatch, useSelector } from "react-redux";
import { InputFile, RootState } from "../../types";
import { MassNotificationResponse } from "../types";
import { post } from "../../../utils/fetch";
import flash from "../../flash/actions";
import { APIError } from "../../../utils/errors";

type Props = {
  setLoading: (value: boolean) => void;
  closeMassNotification: () => void;
};

export const MassNotification = ({ setLoading, closeMassNotification }: Props) => {
  const [allowSend, setAllowSend] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [inputFile, setInputFile] = useState<InputFile | null>(null);
  const [errors, setErrors] = useState<APIError | null>(null);

  const currentOrganization: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );
  const dispatch = useDispatch();

  const validateContent = () => {
    setAllowSend(!!inputFile && message.length > 0);
  };

  const updateMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    validateContent();
  };

  const onUploadFile = (event: any) => {
    setErrors(null);
    setInputFile(event.target.files[0]);
    setAllowSend(message.length > 0);
  };

  const sendMessage = async () => {
    if (!inputFile || !message) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("phone_numbers", inputFile);
    formData.append("message_body", message);

    const url = `/api/organization/${currentOrganization?.id}/sms_batch/`;
    const resp = await post<MassNotificationResponse>(url, formData);

    if (resp.data) {
      let confirmationText = `Mass notification has been sent.`;
      if (resp.data.skippedCount != 0)
        confirmationText = confirmationText.concat(
          `${resp.data.skippedCount} phone number(s) were skipped.`
        );
      dispatch(flash(confirmationText));
      closeMassNotification();
    } else if (resp.error) {
      setErrors(resp.error);
      dispatch(flash(`Mass notification failed to send.`, "error"));
    }

    setLoading(false);
  };

  return (
    <div className="sections pl-20 pr-20 fade">
      <div className="section p-15">
        <h1>Mass Notification</h1>
        <div className="mb-10">
          This tool is designed to send a one way SMS notification to many users. Use the sample
          file provided to input many 10 digit phone numbers. Then use the Browse button to upload
          your file. type your message, and click send.
        </div>
        <div className="mb-30">
          * This message is <span className="bold">not</span> secure. Do not include{" "}
          <span className="bold">PHI</span>. Limit to 160 characters.
        </div>

        <div className="flex-between mb-10">
          <label htmlFor="phone">Phone Numbers:</label>
          <div>
            <a href={sample} className="hyperlink" download="sample.csv">
              Download a sample file
            </a>
          </div>
        </div>

        <input className="mb-30" type="file" name="file" onChange={onUploadFile} />

        {errors && (
          <div className="alert alert-error mt-10 mb-20">
            <div className="bold">{errors?.message}</div>

            {Object.values(errors.fieldErrors).map((row, rowIndex) => (
              <div key={rowIndex} className="flex">
                <div className="flex-1">{row}</div>
              </div>
            ))}
          </div>
        )}

        <label htmlFor="sms-message">Message:</label>

        <TextareaAutosize
          id="sms-message"
          className="form-control text-area mt-10"
          value={message}
          onChange={updateMessage}
          maxLength={160}
        />
        <div className="mt-15 flex-end">
          <button onClick={closeMassNotification} className="mr-10">
            Cancel
          </button>
          <button
            disabled={!allowSend}
            className={allowSend ? "highlight" : ""}
            onClick={sendMessage}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};
